import React from 'react';
import { Card, CardContent } from '@/components/base/Card';
import { ConsultationScrollArea } from '@/components/consultation/ConsultationScrollArea';
import type { AyurvedaData } from '@/ayurveda/types/ayurveda';
import locationIcon from '@/assets/images/env-location.png';
import seasonIcon from '@/assets/images/env-season.png';
import temperatureIcon from '@/assets/images/env-temperature.png';
import humidityIcon from '@/assets/images/env-humidity.png';
import spiceActiveIcon from '@/assets/images/env-spice-active.png';
import spiceInactiveIcon from '@/assets/images/env-spice-inactive.png';

export interface EnvironmentalProfileCardProps {
  desaInformation?: AyurvedaData['desaInformation'];
}

function LocationIcon() {
  return <img src={locationIcon} alt="" className="h-3 w-3" aria-hidden="true" />;
}

function TemperatureIcon() {
  return <img src={temperatureIcon} alt="" className="h-3 w-3" aria-hidden="true" />;
}

function SeasonIcon() {
  return <img src={seasonIcon} alt="" className="h-3 w-3" aria-hidden="true" />;
}

function HumidityIcon() {
  return <img src={humidityIcon} alt="" className="h-3 w-3" aria-hidden="true" />;
}

/** Exact Figma "spicy" icon used for the Regional Diet spice-level row. */
function SpiceIcon({ active }: { active: boolean }) {
  return (
    <img
      src={active ? spiceActiveIcon : spiceInactiveIcon}
      alt=""
      className="h-[18px] w-[18px]"
      aria-hidden="true"
    />
  );
}

function VegIndicator() {
  return (
    <div className="relative h-[18px] w-[18px] shrink-0 rounded-[4px] border border-solid border-[#129E54]">
      <div className="absolute left-1/2 top-1/2 h-2 w-2 -translate-x-1/2 -translate-y-1/2 rounded-full bg-[#129E54]" />
    </div>
  );
}

function StatItem({ icon, label }: { icon: React.ReactNode; label?: string | number | null }) {
  return (
    <div className="flex items-center gap-1">
      <div className="flex h-4 w-4 shrink-0 items-center justify-center rounded-full bg-white">
        {icon}
      </div>
      <span className="whitespace-nowrap text-xs text-[#585859]">
        {label !== undefined && label !== null && label !== '' ? label : '—'}
      </span>
    </div>
  );
}

function DietRow({ label, value }: { label: string; value?: string | null }) {
  return (
    <div className="flex items-center justify-between gap-2">
      <span className="text-sm font-medium text-[#42434B]">{label}:</span>
      <span className={value ? 'text-sm text-[#585859]' : 'text-sm italic text-muted-foreground'}>
        {value || 'No data available'}
      </span>
    </div>
  );
}

export function EnvironmentalProfileCard({ desaInformation }: EnvironmentalProfileCardProps) {
  const regionalDiet = desaInformation?.regionalDiet;
  // Clamp to 0-3 — the row always renders exactly three chilli icons.
  const spiceLevel = Math.max(0, Math.min(3, regionalDiet?.spiceLevel ?? 0));
  const hasRegionalDiet =
    regionalDiet &&
    (regionalDiet.grains ||
      regionalDiet.pulses ||
      regionalDiet.fruits ||
      regionalDiet.veg ||
      regionalDiet.dairy);

  return (
    <Card padding="none" className="h-full overflow-hidden rounded-[24px] border-white bg-white">
      <ConsultationScrollArea className="h-full">
        <CardContent className="flex flex-col gap-3 p-4">
          <h2 className="font-serif text-[calc(20px*var(--app-scale))] font-normal leading-[30px] text-[#3C4F1E]">
            Environmental Profile
          </h2>

          <div className="flex items-center justify-between rounded-2xl bg-[#F8F8F1] px-4 py-2">
            <StatItem icon={<LocationIcon />} label={desaInformation?.city} />
            <StatItem icon={<SeasonIcon />} label={desaInformation?.season} />
            <StatItem
              icon={<TemperatureIcon />}
              label={
                desaInformation?.temperature !== undefined && desaInformation?.temperature !== null
                  ? `${desaInformation.temperature}°C`
                  : null
              }
            />
            <StatItem
              icon={<HumidityIcon />}
              label={
                desaInformation?.humidity !== undefined && desaInformation?.humidity !== null
                  ? `${desaInformation.humidity}%`
                  : null
              }
            />
          </div>

          <div className="flex flex-col gap-2 rounded-2xl bg-[#F8F8F1] px-4 py-2">
            <div className="flex items-center justify-between">
              <span className="text-base font-medium text-[#585859]">Regional Diet:</span>
              <div className="flex items-center gap-0.5">
                <SpiceIcon active={spiceLevel >= 1} />
                <SpiceIcon active={spiceLevel >= 2} />
                <SpiceIcon active={spiceLevel >= 3} />
                <VegIndicator />
              </div>
            </div>
            {hasRegionalDiet ? (
              <div className="flex flex-col gap-2">
                <DietRow label="Grains" value={regionalDiet?.grains} />
                <DietRow label="Pulses" value={regionalDiet?.pulses} />
                <DietRow label="Fruits" value={regionalDiet?.fruits} />
                <DietRow label="Veg" value={regionalDiet?.veg} />
                <DietRow label="Dairy" value={regionalDiet?.dairy} />
              </div>
            ) : (
              <p className="text-sm italic text-muted-foreground">No data available</p>
            )}
          </div>
        </CardContent>
      </ConsultationScrollArea>
    </Card>
  );
}
